import { FC, ChangeEvent } from 'react'
import {
  Control,
  UseFormRegister,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { TBuyerFormValues } from '../types'
import { toCurrencyNoStyle, toValidNumber } from 'lib/number'
import Typography from 'components/Typography'
import FormElement from 'components/form/FormElement'
import Input from 'components/form/Input'
import RadioGroup from 'components/form/RadioGroup'
import styles from './Buyer.module.scss'

export type TProps = {
  register: UseFormRegister<TBuyerFormValues>
  control: Control<TBuyerFormValues>
}

const emptyField = {
  message: 'O preenchimento desse campo é obrigatório',
  value: true,
}

const Buyer: FC<TProps> = ({ register, control }) => {
  const { errors } = useFormState({ control })
  const { paymentType, hasApprovedCredit } = useWatch({ control })

  const isAboutCredit =
    paymentType === 'DEFERRED' || paymentType === 'CONSORTIUM'
  const withApprovedCredit = hasApprovedCredit === 'true'
  const needIncomeMonthly =
    paymentType === 'DEFERRED' && hasApprovedCredit === 'false'

  const getInputStatus = (error?: string) => (error ? 'error' : undefined)

  const changeCPFValue = (value: string) => {
    const validCurrency = value.replace(/\D/g, '')
    if (validCurrency) {
      let formattedValue = validCurrency.replace(/(\d{3})(\d)/, '$1.$2')
      formattedValue = formattedValue.replace(/(\d{3})(\d)/, '$1.$2')
      formattedValue = formattedValue.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      return formattedValue
    } else {
      return ''
    }
  }

  const handleChangeCurrencyInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const validCurrency = toValidNumber(value)
    if (validCurrency) {
      const formattedValue = toCurrencyNoStyle(validCurrency)
      e.target.value = formattedValue
    } else {
      e.target.value = ''
    }
  }

  return (
    <form
      data-testid="ecVisitModalBuyerForm"
      className={styles.ecVisitModalBuyer}
    >
      <div>
        <Typography variant="TitleSmall" bold>
          Você está buscando para você ou outra pessoa?
        </Typography>
        <Typography variant="ParagraphSmall">
          Isso é importante para garantirmos o contato. Caso você seja corretor,
          fazemos parceria dividindo a comissão
        </Typography>
      </div>
      <FormElement
        status={getInputStatus(errors.name?.message)}
        title={{
          variant: 'label',
          value: 'Escolha uma das opções',
          htmlFor: 'mainInterest',
          required: true,
        }}
        helpText={{
          ariaDescribeId: 'mainInterest',
          value: errors.mainInterest?.message,
        }}
      >
        <RadioGroup
          items={[
            {
              label: 'Para mim',
              variant: 'button',
              size: 'small',
              inputElementProps: {
                ...register('mainInterest', { required: emptyField }),
                value: 'MYSELF',
                id: 'mainInterestRadio',
              },
            },
            {
              label: 'Para familiares',
              variant: 'button',
              size: 'small',
              inputElementProps: {
                ...register('mainInterest', { required: emptyField }),
                value: 'RELATIVE',
              },
            },
            {
              label: 'Sou corretor',
              variant: 'button',
              size: 'small',
              inputElementProps: {
                ...register('mainInterest', { required: emptyField }),
                value: 'PARTNER',
              },
            },
          ]}
        />
      </FormElement>

      <div className={styles.ecVisitModalBuyerGrid}>
        <Typography variant="TitleSmall" bold>
          Como você pretende <strong>comprar</strong> o imóvel?
        </Typography>
        <FormElement
          status={getInputStatus(errors.paymentType?.message)}
          title={{
            variant: 'label',
            value: 'Forma de pagamento',
            htmlFor: 'paymentType',
            required: true,
          }}
          helpText={{
            ariaDescribeId: 'paymentType',
            value: errors.paymentType?.message,
          }}
        >
          <RadioGroup
            items={[
              {
                label: 'À vista',
                variant: 'button',
                size: 'small',
                inputElementProps: {
                  ...register('paymentType', { required: emptyField }),
                  value: 'CASH',
                },
              },
              {
                label: 'Financiamento',
                variant: 'button',
                size: 'small',
                inputElementProps: {
                  ...register('paymentType', { required: emptyField }),
                  value: 'DEFERRED',
                  id: 'deferredRadio',
                },
              },
              {
                label: 'Consórcio',
                variant: 'button',
                size: 'small',
                inputElementProps: {
                  ...register('paymentType', { required: emptyField }),
                  value: 'CONSORTIUM',
                  id: 'consortiumRadio',
                },
              },
              {
                label: 'Permuta',
                variant: 'button',
                size: 'small',
                inputElementProps: {
                  ...register('paymentType', { required: emptyField }),
                  value: 'BARTER',
                },
              },
              {
                label: 'Preciso vender para comprar',
                variant: 'button',
                size: 'small',
                inputElementProps: {
                  ...register('paymentType', { required: emptyField }),
                  value: 'NEED_TO_SELL',
                  id: 'needToSellRadio',
                },
              },
            ]}
          />
        </FormElement>

        {isAboutCredit && (
          <FormElement
            status={getInputStatus(errors.hasApprovedCredit?.message)}
            title={{
              variant: 'label',
              value: 'Já possui carta de crédito?',
              htmlFor: 'hasApprovedCredit',
            }}
            helpText={{
              ariaDescribeId: 'hasApprovedCredit',
              value: errors.hasApprovedCredit?.message,
            }}
          >
            <RadioGroup
              items={[
                {
                  label: 'Sim',
                  variant: 'button',
                  size: 'small',
                  inputElementProps: {
                    ...register('hasApprovedCredit', {
                      required: emptyField && isAboutCredit,
                    }),
                    value: 'true',
                    id: 'hasCredit',
                  },
                },
                {
                  label: 'Não',
                  variant: 'button',
                  size: 'small',
                  inputElementProps: {
                    ...register('hasApprovedCredit', {
                      required: emptyField && isAboutCredit,
                    }),
                    value: 'false',
                    id: 'hasNoCredit',
                  },
                },
              ]}
            />
          </FormElement>
        )}

        {isAboutCredit && withApprovedCredit && (
          <FormElement
            status={getInputStatus(errors.creditTotalValue?.message)}
            title={{
              variant: 'label',
              value: 'Qual é o valor da carta?',
              htmlFor: 'creditTotalValue',
            }}
            helpText={{
              ariaDescribeId: 'creditTotalValue',
              value: errors.creditTotalValue?.message,
            }}
          >
            <Input
              leadingContent={{
                variant: 'text',
                value: 'R$',
              }}
              inputElementProps={{
                id: 'creditTotalValue',
                inputMode: 'numeric',
                maxLength: 11,
                ...register('creditTotalValue', {
                  required: emptyField,
                  validate: (value) =>
                    !!value && isAboutCredit && withApprovedCredit,
                  onChange: (e: ChangeEvent<HTMLInputElement>) => {
                    handleChangeCurrencyInput(e)
                  },
                }),
              }}
            />
          </FormElement>
        )}

        {isAboutCredit && hasApprovedCredit === 'false' && (
          <FormElement
            status={getInputStatus(errors.cpf?.message)}
            title={{
              variant: 'label',
              value: 'Qual é o seu CPF?',
              htmlFor: 'cpf',
            }}
            helpText={{
              ariaDescribeId: 'cpf',
              value: errors.cpf?.message
                ? errors.cpf?.message
                : 'CPF é necessário para começarmos o processo de aprovar crédito',
            }}
          >
            <Input
              inputElementProps={{
                id: 'cpf',
                maxLength: 14,
                ...register('cpf', {
                  required: emptyField,
                  validate: (value) =>
                    changeCPFValue(value).length === 14 || 'CPF inválido',
                  onChange: (e: ChangeEvent<HTMLInputElement>) => {
                    e.target.value = changeCPFValue(e.target.value)
                  },
                }),
                required: true,
              }}
            />
          </FormElement>
        )}

        {needIncomeMonthly && (
          <FormElement
            status={getInputStatus(errors.incomeMonthly?.message)}
            title={{
              variant: 'label',
              value: 'Qual é a sua renda mensal?',
              htmlFor: 'incomeMonthly',
            }}
            helpText={{
              ariaDescribeId: 'incomeMonthly',
              value: errors.incomeMonthly?.message,
            }}
          >
            <Input
              leadingContent={{
                variant: 'text',
                value: 'R$',
              }}
              inputElementProps={{
                id: 'incomeMonthly',
                inputMode: 'numeric',
                maxLength: 11,
                ...register('incomeMonthly', {
                  required: emptyField,
                  validate: (value) => !!value && needIncomeMonthly,
                  onChange: (e: ChangeEvent<HTMLInputElement>) => {
                    handleChangeCurrencyInput(e)
                  },
                }),
              }}
            />
          </FormElement>
        )}

        {paymentType === 'NEED_TO_SELL' && (
          <FormElement
            status={'error'}
            title={{
              variant: 'label',
              value: 'Imóvel já está anunciado?',
              htmlFor: 'listingAnnounced',
            }}
            helpText={{
              ariaDescribeId: 'listingAnnounced',
              value: errors.listingAnnounced?.message,
            }}
          >
            <RadioGroup
              items={[
                {
                  label: 'Sim',
                  variant: 'button',
                  size: 'small',
                  inputElementProps: {
                    ...register('listingAnnounced', {
                      required: emptyField,
                    }),
                    value: 'true',
                    id: 'listingAnnouncedYes',
                  },
                },
                {
                  label: 'Não',
                  variant: 'button',
                  size: 'small',
                  inputElementProps: {
                    ...register('listingAnnounced', {
                      required: emptyField,
                    }),
                    value: 'false',
                    id: 'listingAnnouncedNo',
                  },
                },
              ]}
            />
          </FormElement>
        )}
      </div>
    </form>
  )
}

export default Buyer
